// src/seoPresets.js
const BASE_URL = "https://www.musicbase.io";

export const seoPresets = {
  // Home
  "/": {
    title: "MusicBase – Playlist Outreach Made Easy",
    description:
      "Find Spotify playlist curators and email or DM them from one place. Search playlists by genre, filter by email or Instagram, and run outreach campaigns so your tracks actually get heard.",
    canonical: `${BASE_URL}/`,
    openGraph: {
      title: "MusicBase – Playlist Outreach Made Easy",
      description:
        "Find Spotify playlist curators and email or DM them from one place. Search playlists by genre, filter by email or Instagram, and run outreach campaigns so your tracks actually get heard.",
      url: `${BASE_URL}/`,
      type: "website",
      image: `${BASE_URL}/logo.png`,
      siteName: "MusicBase",
    },
    twitter: {
      card: "summary_large_image",
      title: "MusicBase – Playlist Outreach Made Easy",
      description:
        "Search playlists, find curator contacts, and send personal outreach campaigns from your own inbox.",
      image: `${BASE_URL}/logo.png`,
    },
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: "MusicBase",
      url: BASE_URL,
      potentialAction: {
        "@type": "SearchAction",
        // point to /search now, not /playlists
        target: `${BASE_URL}/search?query={search_term_string}`,
        "query-input": "required name=search_term_string",
      },
    },
  },

  // Pricing
  "/pricing": {
    title: "Pricing – MusicBase Playlist Outreach Plans",
    description:
      "Compare MusicBase Basic and Outreach plans for playlist promotion. Start a 3-day free trial, then choose monthly or annual billing for email + IG outreach.",
    canonical: `${BASE_URL}/pricing`,
    openGraph: {
      title: "Pricing – MusicBase Playlist Outreach Plans",
      description:
        "Choose the best MusicBase plan for playlist outreach. Compare Basic and Outreach tiers with features for email and Instagram campaigns.",
      url: `${BASE_URL}/pricing`,
      type: "website",
      image: `${BASE_URL}/logo.png`,
      siteName: "MusicBase",
    },
    twitter: {
      card: "summary_large_image",
      title: "Pricing – MusicBase Playlist Outreach Plans",
      description:
        "Pick the right MusicBase plan for playlist outreach and start a 3-day free trial.",
      image: `${BASE_URL}/logo.png`,
    },
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: "Pricing – MusicBase",
      url: `${BASE_URL}/pricing`,
    },
  },

  // Login
  "/login": {
    title: "Log In – MusicBase",
    description:
      "Log into your MusicBase account to search Spotify playlists, build outreach campaigns, and track curator replies from one dashboard.",
    canonical: `${BASE_URL}/login`,
    openGraph: {
      title: "Log In – MusicBase",
      description:
        "Access your MusicBase account to manage playlist outreach campaigns and curator contacts.",
      url: `${BASE_URL}/login`,
      type: "website",
      image: `${BASE_URL}/logo.png`,
      siteName: "MusicBase",
    },
    twitter: {
      card: "summary_large_image",
      title: "Log In – MusicBase",
      description:
        "Log into MusicBase to continue your playlist outreach and campaigns.",
      image: `${BASE_URL}/logo.png`,
    },
  },

  // Signup
  "/signup": {
    title: "Create Your MusicBase Account",
    description:
      "Sign up for MusicBase to find playlist curators with contact info and send personal outreach emails and DMs from your own inbox.",
    canonical: `${BASE_URL}/signup`,
    openGraph: {
      title: "Create Your MusicBase Account",
      description:
        "Create a MusicBase account to search playlists by genre, reveal curator contacts, and run email campaigns.",
      url: `${BASE_URL}/signup`,
      type: "website",
      image: `${BASE_URL}/logo.png`,
      siteName: "MusicBase",
    },
    twitter: {
      card: "summary_large_image",
      title: "Create Your MusicBase Account",
      description:
        "Sign up for MusicBase to streamline playlist outreach and track replies.",
      image: `${BASE_URL}/logo.png`,
    },
  },

  // Profile / account
  "/profile": {
    title: "Your Account & Subscription – MusicBase",
    description:
      "Manage your MusicBase profile, subscription plan, billing details, and connected mailboxes.",
    canonical: `${BASE_URL}/profile`,
    openGraph: {
      title: "Your Account & Subscription – MusicBase",
      description:
        "Update your MusicBase account details, billing info, and subscription.",
      url: `${BASE_URL}/profile`,
      type: "website",
      image: `${BASE_URL}/logo.png`,
      siteName: "MusicBase",
    },
    twitter: {
      card: "summary_large_image",
      title: "Your Account & Subscription – MusicBase",
      description:
        "Manage your MusicBase profile, billing, and playlist outreach subscription.",
      image: `${BASE_URL}/logo.png`,
    },
  },

  // Spotify Playlist Search
  "/search": {
    title: "Spotify Playlist Search & Curator Emails – MusicBase",
    description:
      "Search Spotify playlists by genre, mood, territory, and contact channel. Reveal curator emails and Instagram handles, then export or add them to outreach campaigns.",
    canonical: `${BASE_URL}/search`,
    openGraph: {
      title: "Spotify Playlist Search & Curator Emails – MusicBase",
      description:
        "Use MusicBase to find playlists with email or IG contact info and build targeted outreach campaigns.",
      url: `${BASE_URL}/search`,
      type: "website",
      image: `${BASE_URL}/logo.png`,
      siteName: "MusicBase",
    },
    twitter: {
      card: "summary_large_image",
      title: "Spotify Playlist Search & Curator Emails – MusicBase",
      description:
        "Search playlists, filter by contact type, and export curators into campaigns.",
      image: `${BASE_URL}/logo.png`,
    },
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: "Spotify Playlist Search – MusicBase",
      url: `${BASE_URL}/search`,
    },
  },

  // Campaigns overview
  "/campaign": {
    title: "Playlist Outreach Campaigns & Email Sequences – MusicBase",
    description:
      "Set up multi-step email campaigns for playlist outreach, send from Gmail, and track delivered, bounced, and replied stats for every curator you pitch.",
    canonical: `${BASE_URL}/campaign`,
    openGraph: {
      title: "Playlist Outreach Campaigns & Email Sequences – MusicBase",
      description:
        "Create, clone, and analyze playlist outreach campaigns with detailed email performance metrics.",
      url: `${BASE_URL}/campaign`,
      type: "website",
      image: `${BASE_URL}/logo.png`,
      siteName: "MusicBase",
    },
    twitter: {
      card: "summary_large_image",
      title: "Playlist Outreach Campaigns & Email Sequences – MusicBase",
      description:
        "Automate playlist outreach campaigns and track replies from your own mailbox.",
      image: `${BASE_URL}/logo.png`,
    },
  },

  // Campaign steps (used as base preset inside CampaignSteps.jsx)
  "/campaign/:id/steps": {
    title: "Campaign Steps & Sequence Editor – MusicBase",
    description:
      "Edit email steps, timing, and templates for your MusicBase playlist outreach campaign.",
    canonical: `${BASE_URL}/campaign`,
    openGraph: {
      title: "Campaign Steps & Sequence Editor – MusicBase",
      description:
        "Fine-tune your playlist outreach campaign steps, follow-ups, and sending windows.",
      url: `${BASE_URL}/campaign`,
      type: "website",
      image: `${BASE_URL}/logo.png`,
      siteName: "MusicBase",
    },
    twitter: {
      card: "summary_large_image",
      title: "Campaign Steps & Sequence Editor – MusicBase",
      description:
        "Manage templates and steps for your playlist outreach campaigns.",
      image: `${BASE_URL}/logo.png`,
    },
  },

  // Single step editor (CampaignStepEdit)
  "/campaign/:id/steps/:stepNo": {
    title: "Edit Campaign Step – MusicBase",
    description:
      "Edit the content, timing, and settings for a single step in your MusicBase playlist outreach campaign.",
    canonical: `${BASE_URL}/campaign`,
    openGraph: {
      title: "Edit Campaign Step – MusicBase",
      description:
        "Adjust a specific step in your playlist outreach campaign sequence.",
      url: `${BASE_URL}/campaign`,
      type: "website",
      image: `${BASE_URL}/logo.png`,
      siteName: "MusicBase",
    },
    twitter: {
      card: "summary_large_image",
      title: "Edit Campaign Step – MusicBase",
      description:
        "Fine-tune an individual step in your playlist outreach campaign.",
      image: `${BASE_URL}/logo.png`,
    },
  },

  // Mailbox / settings
  "/settings/mail": {
    title: "Connect Mailboxes for Outreach – MusicBase",
    description:
      "Connect Gmail or other mailboxes so MusicBase can send playlist outreach campaigns directly from your own inbox.",
    canonical: `${BASE_URL}/settings/mail`,
    openGraph: {
      title: "Connect Mailboxes for Outreach – MusicBase",
      description:
        "Manage the email accounts MusicBase uses to send playlist outreach campaigns.",
      url: `${BASE_URL}/settings/mail`,
      type: "website",
      image: `${BASE_URL}/logo.png`,
      siteName: "MusicBase",
    },
    twitter: {
      card: "summary_large_image",
      title: "Connect Mailboxes for Outreach – MusicBase",
      description:
        "Link your email accounts so MusicBase can send playlist outreach from your inbox.",
      image: `${BASE_URL}/logo.png`,
    },
  },

  // Contact
  "/contact": {
    title: "Contact MusicBase Support",
    description:
      "Questions about playlist outreach or your MusicBase account? Contact support for billing, product questions, or data removal requests.",
    canonical: `${BASE_URL}/contact`,
    openGraph: {
      title: "Contact MusicBase Support",
      description:
        "Get in touch with MusicBase for help with playlist outreach, subscriptions, and data requests.",
      url: `${BASE_URL}/contact`,
      type: "website",
      image: `${BASE_URL}/logo.png`,
      siteName: "MusicBase",
    },
    twitter: {
      card: "summary_large_image",
      title: "Contact MusicBase Support",
      description:
        "Reach out to MusicBase with product, billing, or privacy questions.",
      image: `${BASE_URL}/logo.png`,
    },
  },

  // Privacy Policy
  "/privacy": {
    title: "Privacy Policy – MusicBase",
    description:
      "Learn how MusicBase collects, uses, and protects your data, including Gmail integrations and playlist outreach activity.",
    canonical: `${BASE_URL}/privacy`,
    openGraph: {
      title: "Privacy Policy – MusicBase",
      description:
        "Read how MusicBase handles personal data, email integrations, and playlist outreach information.",
      url: `${BASE_URL}/privacy`,
      type: "website",
      image: `${BASE_URL}/logo.png`,
      siteName: "MusicBase",
    },
    twitter: {
      card: "summary_large_image",
      title: "Privacy Policy – MusicBase",
      description:
        "Understand how your data is stored and used when you use MusicBase.",
      image: `${BASE_URL}/logo.png`,
    },
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "PrivacyPolicy",
      name: "Privacy Policy – MusicBase",
      url: `${BASE_URL}/privacy`,
    },
  },

  // Terms of Service
  "/terms": {
    title: "Terms of Service – MusicBase",
    description:
      "Read the terms that govern your use of the MusicBase playlist outreach platform, including acceptable use and subscription rules.",
    canonical: `${BASE_URL}/terms`,
    openGraph: {
      title: "Terms of Service – MusicBase",
      description:
        "Review the terms that apply when you use MusicBase for playlist outreach.",
      url: `${BASE_URL}/terms`,
      type: "website",
      image: `${BASE_URL}/logo.png`,
      siteName: "MusicBase",
    },
    twitter: {
      card: "summary_large_image",
      title: "Terms of Service – MusicBase",
      description:
        "Read the MusicBase terms covering subscriptions, acceptable use, and liability.",
      image: `${BASE_URL}/logo.png`,
    },
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: "Terms of Service – MusicBase",
      url: `${BASE_URL}/terms`,
    },
  },

  // Forgot password (request reset link)
  "/forgot-password": {
    title: "Forgot Your Password – MusicBase",
    description:
      "Request a password reset link for your MusicBase account so you can get back to playlist outreach.",
    canonical: `${BASE_URL}/forgot-password`,
    openGraph: {
      title: "Forgot Your Password – MusicBase",
      description:
        "Use this page to request a secure password reset link for your MusicBase account.",
      url: `${BASE_URL}/forgot-password`,
      type: "website",
      image: `${BASE_URL}/logo.png`,
      siteName: "MusicBase",
    },
    twitter: {
      card: "summary_large_image",
      title: "Forgot Your Password – MusicBase",
      description:
        "Request a MusicBase password reset link.",
      image: `${BASE_URL}/logo.png`,
    },
  },

  // Reset password (set new password from token)
  "/reset-password": {
    title: "Choose a New Password – MusicBase",
    description:
      "Set a new password for your MusicBase account and regain access to your playlist outreach dashboard.",
    canonical: `${BASE_URL}/reset-password`,
    openGraph: {
      title: "Choose a New Password – MusicBase",
      description:
        "Use this page to securely choose a new password for your MusicBase account.",
      url: `${BASE_URL}/reset-password`,
      type: "website",
      image: `${BASE_URL}/logo.png`,
      siteName: "MusicBase",
    },
    twitter: {
      card: "summary_large_image",
      title: "Choose a New Password – MusicBase",
      description:
        "Reset your MusicBase account password.",
      image: `${BASE_URL}/logo.png`,
    },
  },

  // Verify code
  "/verify-code": {
    title: "Verify Your Email – MusicBase",
    description:
      "Enter the 6-digit verification code sent to your email to finish creating your MusicBase account.",
    canonical: `${BASE_URL}/verify-code`,
    openGraph: {
      title: "Verify Your Email – MusicBase",
      description:
        "Confirm your email address to complete MusicBase signup and start playlist outreach.",
      url: `${BASE_URL}/verify-code`,
      type: "website",
      image: `${BASE_URL}/logo.png`,
      siteName: "MusicBase",
    },
    twitter: {
      card: "summary_large_image",
      title: "Verify Your Email – MusicBase",
      description:
        "Verify your email to finalize your MusicBase account.",
      image: `${BASE_URL}/logo.png`,
    },
  },

  // Verify email (link-click page)
  "/verify-email": {
    title: "Check Your Email – MusicBase",
    description:
      "We’ve sent a verification link to your inbox. Click the link to confirm your email and start using MusicBase.",
    canonical: `${BASE_URL}/verify-email`,
    openGraph: {
      title: "Check Your Email – MusicBase",
      description:
        "Open the verification email from MusicBase and click the link to activate your account.",
      url: `${BASE_URL}/verify-email`,
      type: "website",
      image: `${BASE_URL}/logo.png`,
      siteName: "MusicBase",
    },
    twitter: {
      card: "summary_large_image",
      title: "Check Your Email – MusicBase",
      description:
        "Confirm your MusicBase account by clicking the link we sent to your inbox.",
      image: `${BASE_URL}/logo.png`,
    },
  },

  // Set password (after email verification)
  "/set-password": {
    title: "Create a Password – MusicBase",
    description:
      "Create a secure password to finish setting up your MusicBase account and start using playlist outreach tools.",
    canonical: `${BASE_URL}/set-password`,
    openGraph: {
      title: "Create a Password – MusicBase",
      description:
        "Set your MusicBase password to access playlist search, campaigns, and curator contacts.",
      url: `${BASE_URL}/set-password`,
      type: "website",
      image: `${BASE_URL}/logo.png`,
      siteName: "MusicBase",
    },
    twitter: {
      card: "summary_large_image",
      title: "Create a Password – MusicBase",
      description:
        "Finish setting up your MusicBase account by creating a password.",
      image: `${BASE_URL}/logo.png`,
    },
  },

  // Buy credits (if used)
  "/buy-credits": {
    title: "Buy Reveal Credits – MusicBase",
    description:
      "Purchase extra reveal credits in MusicBase to unlock curator emails and Instagram contacts for more playlists.",
    canonical: `${BASE_URL}/buy-credits`,
    openGraph: {
      title: "Buy Reveal Credits – MusicBase",
      description:
        "Top up your reveal credits to unlock more playlist curator contact details in MusicBase.",
      url: `${BASE_URL}/buy-credits`,
      type: "website",
      image: `${BASE_URL}/logo.png`,
      siteName: "MusicBase",
    },
    twitter: {
      card: "summary_large_image",
      title: "Buy Reveal Credits – MusicBase",
      description:
        "Get more credits to reveal playlist curator emails and IG handles.",
      image: `${BASE_URL}/logo.png`,
    },
  },
};
